import React from 'react';
import styled from 'styled-components';
import Card, { CardTitle } from '@/components/Card';
import PaginationComponent from '@/components/Pagination';
import { Order } from '@/apis/order/interface';
import SelectComponent from '@/components/SelectComponent';
import { filterLimitList } from '@/assets/data/selectOptions';
import useTimer from '@/hooks/common/useTimer';
import theme from '@/styles/theme';
import { getRowBgColor } from '@/utils/tableFormat';

type ListType = Order | any;

interface Props {
  tableKey?: string;
  data: Array<ListType>;
  headers: Array<TableHeader>;
  rowDataFormat?: (item: ListType, alias: string) => any;
  tableHeader?: string;
  page: number;
  totalCount: number;
  setPage: (page: number) => void;
  itemsCountPerPage: number;
  onClick: (id: number, item?: ListType) => void;
  highLightCheck?: (item?: ListType) => boolean;
  limitFilter?: (e: number) => void;
}

const Table = (props: Props) => {
  const {
    tableKey,
    data,
    headers,
    rowDataFormat,
    tableHeader,
    page,
    totalCount,
    setPage,
    itemsCountPerPage,
    onClick,
    highLightCheck,
    limitFilter
  } = props;

  const column = Array.from({ length: 10 }, () => new Object());
  const newArray: Array<ListType> = Object.assign(column, data);

  const renderTableHeader = () => (
    <TableHeader>
      {headers.map((header: TableHeader, index: number) => (
        <HeaderItem flex={header.flex} key={header.alias + 'head' + index}>
          {typeof header.name === 'string' ? (
            <TableHeaderText>{header.name}</TableHeaderText>
          ) : (
            header.name
          )}
        </HeaderItem>
      ))}
    </TableHeader>
  );

  const renderTimer = (
    header: TableHeader,
    item: ListType,
    index: number,
    key: number
  ) => {
    return (
      <TimerRow
        header={header}
        cancelScheduledAt={item.cancelScheduledAt}
        cancelBy={item.cancelBy}
        key={header.alias + 'body' + index + 'sendAt' + key}
      />
    );
  };

  const renderTableBody = () => {
    return newArray.map((item: ListType, key: number) => {
      return (
        <TableBody
          key={tableKey ? tableKey + key : key}
          onClick={() => onClick(item.id, item)}>
          <TableBodyRow
            isHighLight={highLightCheck ? highLightCheck(item) : false}
            bgColor={getRowBgColor?.(item) ?? theme.DF_COLOR.WHITE}>
            {!!Object.keys(item).length &&
              headers.map((header: TableHeader, index: number) => {
                if (header.alias === 'sendAt') {
                  return renderTimer(header, item, index, key);
                }
                return (
                  <TableBodyRowData
                    flex={header.flex}
                    left={header.left}
                    key={header.alias + 'body' + index + 'key' + key}>
                    <TableBodyText>
                      {rowDataFormat
                        ? rowDataFormat(item, header.alias)
                        : item?.[header.alias]}
                    </TableBodyText>
                  </TableBodyRowData>
                );
              })}
          </TableBodyRow>
        </TableBody>
      );
    });
  };

  return (
    <Wrapper>
      {tableHeader && (
        <TitleWrapper>
          <CardTitle>{tableHeader}</CardTitle>
          {!!limitFilter && (
            <SelectComponent
              options={filterLimitList}
              defaultValue={filterLimitList[0]}
              onChange={limitFilter}
            />
          )}
        </TitleWrapper>
      )}

      {/* Table */}
      <Card>
        <TableWrapper>
          {renderTableHeader()}
          {renderTableBody()}
        </TableWrapper>
      </Card>
      <PaginationWrapper>
        <PaginationComponent
          setPage={setPage}
          totalCount={totalCount || 10}
          page={page}
          itemsCountPerPage={itemsCountPerPage}
        />
      </PaginationWrapper>
    </Wrapper>
  );
};

export default Table;

const TimerRow = ({
  header,
  cancelScheduledAt,
  cancelBy
}: {
  header: TableHeader;
  cancelScheduledAt: Date | null;
  cancelBy: string | null;
}) => {
  const orderEnableTime = new Date(cancelScheduledAt!).getTime();
  const nowTime = new Date().getTime();
  const gapSeconds = orderEnableTime - nowTime;
  const isEnabledTime = nowTime < orderEnableTime;
  const remainingTime = gapSeconds / 1000;

  const initMin = Math.floor(remainingTime / 60);
  const initSec = Math.floor(remainingTime % 60);
  const { min, sec } = useTimer({
    active: isEnabledTime,
    initMin: initMin,
    initSec: initSec
  });

  const orderSendAtColor = !isEnabledTime
    ? theme.DF_COLOR.GRAY400
    : min < 1 && Number(sec) <= 59
    ? theme.DF_COLOR.RED700
    : theme.DF_COLOR.BLACK;

  let result;

  if (isEnabledTime) {
    result = `${min ? `${min}분` : ''} ${sec}초 남음`;
  } else {
    if (cancelBy === 'admin') {
      result = '무응답';
    } else if (cancelBy === 'user') {
      result = '환자 취소';
    } else if (cancelBy === 'pharmacist') {
      result = '약사 취소';
    } else {
      result = '';
    }
  }

  return (
    <TableBodyRowData flex={header.flex} left={header.left}>
      <TableBodyText color={orderSendAtColor}>{result}</TableBodyText>
    </TableBodyRowData>
  );
};

const Wrapper = styled.div``;

const TitleWrapper = styled.div`
  display: flex;
  place-content: space-between;
  margin-bottom: 5px;
`;

const TableWrapper = styled.div`
  background: #fff;
  border-radius: 5px;
`;

const TableHeader = styled.div`
  width: 100%;
  height: 46px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #f1f1f1;
  padding: 0 10px;
`;

const HeaderItem = styled.div<{ flex: number }>`
  ${({ flex }) => `flex: ${flex}`};
  text-align: center;
  padding: 0 10px;
`;

const TableBody = styled.div`
  height: 100%;
  position: relative;
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
`;

const TableBodyRow = styled.div<{
  isHighLight: boolean;
  bgColor: string;
}>`
  display: flex;
  flex-flow: row;
  width: 100%;
  min-height: 72px;
  align-items: center;
  border-bottom: 1px solid #f1f1f1;
  cursor: pointer;
  padding: 16px 10px;

  &:hover {
    opacity: 0.7;
  }

  ${({ bgColor }) => `
    background: ${bgColor};
  `};
`;

const TableBodyRowData = styled.div<{ flex: number; left?: boolean }>`
  ${({ flex }) => `flex: ${flex}`};
  text-align: ${({ left }) => (left ? 'left' : 'center')};
  margin: 0 10px;
`;

const PaginationWrapper = styled.div``;

const TableHeaderText = styled.span`
  ${({ theme }) => theme.DF_TYPOGRAPHY.UI17R};
  color: ${({ theme }) => theme.DF_COLOR.GRAY400};
  display: inline-flex;
  align-items: center;
`;

const TableBodyText = styled.span<{ color?: string }>`
  ${({ theme }) => theme.DF_TYPOGRAPHY.UI17R};
  color: ${({ theme, color }) => (!!color ? color : theme.DF_COLOR.GRAY900)};
  display: inline-flex;
  align-items: center;
`;
